// The developer copy's render helpers (docs/launch/developer-page.md as written, every line from the claims ledger,
// docs/launch/claims.md; lines waiting on a fact stay off the page, launch-copy.ts) and /developers, now The Forge zone.

import { h, link } from "../dom.ts";
import { FACTS, fill, inline, type Action, type Block, type Facts } from "../launch-copy.ts";
import { zonePage } from "../zones/page.ts";
import { zoneById } from "../zones/registry.ts";

/**
 * `?held=1` on a dev server (a dotless host name or a bare IP, never banger.sh) shows held lines with their
 * placeholders, for review.
 */
export function showHeld(): boolean {
  return /^[^.]+$|^(\d+\.){3}\d+$|^\[[0-9a-f:]+\]$/i.test(location.hostname) && new URLSearchParams(location.search).get("held") === "1";
}

/** A rendering context: the facts (static plus live) and whether held lines show. */
export interface Ctx {
  facts: Facts;
  held: boolean;
}

export const ctxWith = (live: Facts): Ctx => ({ facts: { ...FACTS, ...live }, held: showHeld() });

/** True when a `when` gate passes (a gate line fills to something non-empty). */
export function passes(when: string | undefined, c: Ctx): boolean {
  if (!when || c.held) return true;
  return !!fill(when, c.facts);
}

/** One line of copy as DOM, or null while it's held. */
export function line<K extends keyof HTMLElementTagNameMap>(tag: K, text: string, c: Ctx, attrs: Record<string, string> | null = null): HTMLElementTagNameMap[K] | null {
  const t = fill(text, c.facts, c.held);
  if (!t) return null;
  return h(
    tag,
    attrs,
    inline(t).map((r) => (r.t === "b" ? h("strong", null, r.s) : r.t === "code" ? h("code", null, r.s) : r.t === "em" ? h("em", null, r.s) : r.s)),
  );
}

export function actionLinks(actions: Action[] | undefined, c: Ctx, primary = true): HTMLElement | null {
  const shown = (actions ?? []).filter((a) => passes(a.when, c));
  if (!shown.length) return null;
  return h(
    "div",
    { class: "copy-actions" },
    shown.map((a, i) =>
      a.href.startsWith("#") ? h("a", { class: `btn${i || !primary ? " ghost" : ""}`, href: a.href }, a.text) : link(a.href, { class: `btn${i || !primary ? " ghost" : ""}` }, a.text),
    ),
  );
}

/** A copy block: heading, paragraphs, list, steps, code and buttons. Null when held or when nothing in it shows. */
export function block(b: Block, c: Ctx, extra: (HTMLElement | null)[] = []): HTMLElement | null {
  if (!passes(b.when, c)) return null;
  const paras = (b.paras ?? []).map((p) => line("p", p, c)).filter(Boolean);
  const list = (b.list ?? []).map((p) => line("li", p, c)).filter(Boolean);
  const steps = (b.steps ?? []).map((p) => line("li", p, c)).filter(Boolean);
  const codeLines = (b.code ?? []).map((l) => fill(l, c.facts, c.held));
  const code = codeLines.length && codeLines.every((l) => l !== null) ? h("pre", { class: "code" }, h("code", null, codeLines.join("\n"))) : null;
  if (!paras.length && !list.length && !steps.length && !code && !extra.some(Boolean)) return null;
  return h(
    "section",
    { class: "copy-block", id: b.id ?? null },
    b.heading ? h("h2", null, b.heading) : null,
    paras,
    list.length ? h("ul", null, list) : null,
    steps.length ? h("ol", { class: "steps" }, steps) : null,
    code,
    extra,
    actionLinks(b.actions, c, false),
  );
}

/** Web games listed on Banger, for "We've listed N web games" (our own Plaza doesn't count). */
export function listedCount(games: { runtime: string; firstParty?: boolean; status?: string }[]): number {
  return games.filter((g) => g.runtime === "web" && !g.firstParty && g.status !== "coming_soon").length;
}

/**
 * /developers is The Forge (zones/developers/zone.json, its sections in zones/developers/): the developer page's copy
 * as a zone, with the signed-out "For creators" landing merged in. The zone's own module (imported by main.ts)
 * registers the sections.
 */
export function developersPage(): HTMLElement {
  const z = zoneById("developers");
  if (!z) throw new Error("That page doesn't exist.");
  return zonePage(z);
}
